import React, { useState } from 'react';
import { useAnnouncements, ANNOUNCEMENT_CATEGORIES } from '../contexts/AnnouncementContext';
import AnnouncementForm from './AnnouncementForm';
import './AdminAnnouncements.css';

function AdminAnnouncements() {
  const { announcements, loading, error, deleteAnnouncement } = useAnnouncements();
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [filter, setFilter] = useState('All');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionMessage, setActionMessage] = useState('');
  
  const filteredAnnouncements = filter === 'All' 
    ? announcements 
    : announcements.filter(announcement => announcement.category === filter);
    
  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleFormCancel = () => {
    setShowForm(false);
    setEditingAnnouncement(null);
  };
  
  const handleDelete = async (id) => {
    try {
      await deleteAnnouncement(id);
      setActionMessage('Announcement deleted successfully');
      setDeleteConfirm(null);
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setActionMessage('');
      }, 3000);
    } catch (err) {
      setActionMessage(`Error: ${err.message}`);
    }
  };
  
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div className="admin-announcements">
      <div className="admin-section-header">
        <div className="header-title">
          <h3>Manage Announcements</h3>
          <p className="subtitle">Create, edit, and manage all NSTP announcements</p>
        </div>
        {!showForm && (
          <button 
            className="btn btn-primary create-btn" 
            onClick={() => setShowForm(true)}
            style={{
              fontSize: '1.1rem',
              padding: '0.9rem 1.8rem',
              fontWeight: '700'
            }}
          >
            <span className="button-icon">+</span>
            Create New Announcement
          </button>
        )}
      </div>
      
      {actionMessage && (
        <div className={`action-message ${actionMessage.includes('Error') ? 'error' : 'success'}`}>
          {actionMessage}
        </div>
      )}
      
      {showForm && (
        <div className="announcement-form-container" style={{ marginBottom: '2rem' }}>
          <AnnouncementForm 
            onCancel={handleFormCancel} 
            announcementToEdit={editingAnnouncement}
          />
        </div>
      )}
      
      <div className="filter-controls announcement-filters">
        <label>Filter by Category:</label>
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filter === 'All' ? 'active' : ''}`}
            onClick={() => setFilter('All')}
          >
            <span className="filter-icon">🔍</span>
            All
          </button>
          {ANNOUNCEMENT_CATEGORIES.map(category => {
            let icon = '📢'; // Default icon
            
            // Set specific icons for each category
            if (category === 'ROTC') icon = '🪖';
            if (category === 'CWTS') icon = '🤝';
            if (category === 'LTS') icon = '🧠';
            if (category === 'General') icon = '📋';
            if (category === 'Important') icon = '⚠️';
            if (category === 'Event') icon = '📅';
            
            return (
              <button 
                key={category}
                className={`filter-btn ${filter === category ? 'active' : ''}`}
                onClick={() => setFilter(category)}
              >
                <span className="filter-icon">{icon}</span>
                {category}
              </button>
            );
          })}
        </div>
      </div>
      
      {loading ? (
        <div className="loading-message">Loading announcements...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : filteredAnnouncements.length === 0 ? (
        <div className="no-announcements-message">
          {filter === 'All' 
            ? 'No announcements created yet.' 
            : `No announcements in the ${filter} category.`}
        </div>
      ) : (
        <div className="announcements-table-container">
          <table className="announcements-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Created</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAnnouncements.map(announcement => (
                <tr key={announcement.id}>
                  <td className="announcement-title-cell">{announcement.title}</td>
                  <td>
                    <span className={`badge badge-${announcement.category ? announcement.category.toLowerCase() : 'general'}`}>
                      {announcement.category || 'General'}
                    </span>
                  </td>
                  <td className="date-cell">{formatDate(announcement.createdAt)}</td>
                  <td className="date-cell">{formatDate(announcement.updatedAt)}</td>
                  <td className="action-cell">
                    {deleteConfirm === announcement.id ? (
                      <div className="delete-confirm">
                        <span>Are you sure?</span>
                        <button 
                          className="btn btn-sm btn-danger" 
                          onClick={() => handleDelete(announcement.id)}
                        >
                          Yes
                        </button>
                        <button 
                          className="btn btn-sm btn-secondary" 
                          onClick={() => setDeleteConfirm(null)}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <>
                        <button 
                          className="btn btn-sm btn-secondary" 
                          onClick={() => handleEdit(announcement)}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn btn-sm btn-danger" 
                          onClick={() => setDeleteConfirm(announcement.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add a direct post announcement button at the bottom if there's no form showing */}
      {!showForm && (
        <div className="post-announcement-container" style={{ 
          display: 'flex',
          justifyContent: 'center',
          marginTop: '2rem'
        }}>
          <button 
            className="btn btn-primary create-btn" 
            onClick={() => setShowForm(true)}
            style={{
              fontSize: '1.1rem',
              padding: '1rem 2.5rem',
              background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
              fontWeight: '700'
            }}
          >
            <span className="button-icon">📝</span>
            Post New Announcement
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminAnnouncements; 